import { Box, Skeleton, alpha, useTheme } from "@mui/material";

/**
 * Skeleton for the left panel (folders accordion) on /training/courses.
 * Matches: search toolbar + scrollable tree area with folder/course items.
 */
export const CoursesLeftPanelSkeleton: React.FC = () => {
	const theme = useTheme();
	return (
		<Box
			sx={{
				flex: 1,
				minHeight: 0,
				bgcolor: "background.paper",
				borderRadius: 2,
				border: `1px solid ${theme.palette.divider}`,
				overflow: "hidden",
				display: "flex",
				flexDirection: "column",
			}}
		>
			{/* Toolbar area - matches search + actions row */}
			<Box
				sx={{
					flexShrink: 0,
					px: 2,
					py: 1.5,
					borderBottom: `1px solid ${theme.palette.divider}`,
					display: "flex",
					alignItems: "center",
					gap: 1.5,
				}}
			>
				<Skeleton
					variant="rounded"
					sx={{ flex: 1, minWidth: 220 }}
					height={40}
				/>
				<Skeleton variant="text" width={60} height={20} />
				<Skeleton variant="rounded" width={36} height={36} />
			</Box>
			{/* Tree area - folder/course items */}
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflow: "hidden",
					py: 0.5,
					px: 1,
				}}
			>
				{[1, 2, 3].map((folderIdx) => (
					<Box key={folderIdx} sx={{ mb: 0.5 }}>
						{/* Folder row */}
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								py: 0.75,
								px: 0.5,
							}}
						>
							<Skeleton variant="rounded" width={20} height={20} />
							<Skeleton
								variant="text"
								width={`${60 + folderIdx * 10}%`}
								height={20}
							/>
						</Box>
						{/* Course items under folder */}
						<Box sx={{ pl: 3 }}>
							{[1, 2].map((courseIdx) => (
								<Box
									key={courseIdx}
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										py: 0.5,
										px: 0.5,
										bgcolor: alpha(theme.palette.action.hover, 0.15),
										borderRadius: 1,
										mb: 0.25,
									}}
								>
									<Skeleton variant="rounded" width={18} height={18} />
									<Skeleton variant="text" width="70%" height={18} />
								</Box>
							))}
						</Box>
					</Box>
				))}
			</Box>
		</Box>
	);
};

/**
 * Skeleton for the right panel (UpcomingSessions) on /training/courses.
 * Matches: header + scrollable list of session cards.
 */
export const CoursesRightPanelSkeleton: React.FC = () => {
	return (
		<Box
			sx={{
				flex: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			{/* Header */}
			<Box
				sx={{
					flexShrink: 0,
					py: 1.5,
					px: 0,
					borderBottom: 1,
					borderColor: "divider",
				}}
			>
				<Skeleton variant="text" width={100} height={28} />
				<Skeleton variant="text" width={180} height={20} sx={{ mt: 0.5 }} />
			</Box>
			{/* Session cards list */}
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflow: "hidden",
					py: 2,
				}}
			>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
					{[1, 2, 3, 4, 5, 6].map((i) => (
						<Box
							key={`session-${i}`}
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1.5,
								p: 1.5,
								borderRadius: 2,
								border: "1px solid",
								borderColor: "divider",
							}}
						>
							<Skeleton variant="rounded" width={36} height={36} />
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Skeleton variant="text" width="75%" height={20} />
								<Skeleton
									variant="text"
									width="45%"
									height={16}
									sx={{ mt: 0.25 }}
								/>
							</Box>
							<Skeleton variant="rounded" width={54} height={36} />
						</Box>
					))}
				</Box>
			</Box>
		</Box>
	);
};

/**
 * Skeleton for course session detail view (CourseSession).
 */
export const CourseSessionSkeleton: React.FC = () => {
	return (
		<Box
			sx={{
				flex: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<Box
				sx={{
					flexShrink: 0,
					display: "flex",
					alignItems: "center",
					gap: 2,
					py: 1.5,
					px: 0,
					borderBottom: 1,
					borderColor: "divider",
				}}
			>
				<Skeleton variant="rounded" width={36} height={36} />
				<Box sx={{ flex: 1 }}>
					<Skeleton variant="text" width={200} height={24} />
					<Skeleton variant="text" width={150} height={18} sx={{ mt: 0.5 }} />
				</Box>
			</Box>
			<Box sx={{ flex: 1, minHeight: 0, py: 2 }}>
				<Skeleton
					variant="rectangular"
					height={120}
					sx={{ borderRadius: 1.5, mb: 2 }}
				/>
				<Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
				<Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
			</Box>
		</Box>
	);
};

/**
 * Skeleton for course detail view (TrainingCourse) - header + sessions list area.
 */
export const CourseDetailSkeleton: React.FC = () => {
	return (
		<Box
			sx={{
				flex: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<Box
				sx={{
					flexShrink: 0,
					display: "flex",
					alignItems: "center",
					gap: 2,
					py: 1.5,
					px: 0,
					borderBottom: 1,
					borderColor: "divider",
				}}
			>
				<Skeleton variant="rounded" width={36} height={36} />
				<Box sx={{ flex: 1 }}>
					<Skeleton variant="text" width={180} height={24} />
					<Skeleton variant="text" width={120} height={18} sx={{ mt: 0.5 }} />
				</Box>
			</Box>
			<Box sx={{ flex: 1, minHeight: 0, py: 2 }}>
				<Skeleton
					variant="rectangular"
					height={48}
					sx={{ borderRadius: 1, mb: 1 }}
				/>
				<Skeleton
					variant="rectangular"
					height={48}
					sx={{ borderRadius: 1, mb: 1 }}
				/>
				<Skeleton
					variant="rectangular"
					height={48}
					sx={{ borderRadius: 1, mb: 1 }}
				/>
				<Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
			</Box>
		</Box>
	);
};

/** Same layout as courses – used for /training/drills left panel. */
export const DrillsLeftPanelSkeleton = CoursesLeftPanelSkeleton;

/** Same layout as course detail – used for TrainingDrill loading. */
export const DrillDetailSkeleton = CourseDetailSkeleton;

/** Same layout as course session – used for DrillSession loading. */
export const DrillSessionSkeleton = CourseSessionSkeleton;
